import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, Filter, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const Pill = ({ children, color = "emerald" }) => (
  <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold bg-${color}-100 text-${color}-900`}>
    {children}
  </span>
);

const NewBatchForm = ({ onCreated, onClose }) => {
  const [form, setForm] = useState({
    batch_code: "",
    denomination: 1000,
    substrate: "polymer",
    production_site: "Quezon City Mint",
    printed_notes: 1000000,
    accepted_notes: 990000,
    electricity_kwh: 60000,
    water_m3: 250,
    packaging_kg: 900,
    substrate_kg: 700,
    transport_km: 400,
    estimated_note_life_years: 5,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/batches", form);
      toast.success(`Batch ${r.data.batch_code} created`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const F = ({ name, label, type = "text", ...rest }) => (
    <div>
      <label className="overline text-slate-500 block mb-1">{label}</label>
      <input
        data-testid={`new-batch-${name}`}
        type={type}
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: type === "number" ? Number(e.target.value) : e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 text-sm font-mono outline-none focus:border-emerald-700"
        {...rest}
      />
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <F name="batch_code" label="Batch Code" required />
        <div>
          <label className="overline text-slate-500 block mb-1">Denomination</label>
          <select
            data-testid="new-batch-denomination"
            value={form.denomination}
            onChange={(e) => setForm({ ...form, denomination: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-slate-300 text-sm bg-white"
          >
            {[20,50,100,200,500,1000].map((d) => <option key={d} value={d}>₱{d}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-slate-500 block mb-1">Substrate</label>
          <select
            data-testid="new-batch-substrate"
            value={form.substrate}
            onChange={(e) => setForm({ ...form, substrate: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 text-sm bg-white"
          >
            <option value="polymer">Polymer</option>
            <option value="paper">Paper (cotton/abaca)</option>
          </select>
        </div>
        <F name="production_site" label="Site" />
        <F name="printed_notes" label="Printed Notes" type="number" />
        <F name="accepted_notes" label="Accepted Notes" type="number" />
        <F name="electricity_kwh" label="Electricity (kWh)" type="number" />
        <F name="water_m3" label="Water (m³)" type="number" />
        <F name="packaging_kg" label="Packaging (kg)" type="number" />
        <F name="substrate_kg" label="Substrate (kg)" type="number" />
        <F name="transport_km" label="Transport (km)" type="number" />
        <F name="estimated_note_life_years" label="Est. Note Life (years)" type="number" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-slate-300 hover:bg-slate-100">Cancel</button>
        <button data-testid="new-batch-submit" type="submit" disabled={busy} className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900 inline-flex items-center gap-2">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Calculate & Save
        </button>
      </div>
    </form>
  );
};

export default function Batches() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [denom, setDenom] = useState("");
  const [substrate, setSubstrate] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (denom) params.denomination = denom;
    if (substrate) params.substrate = substrate;
    api.get("/batches", { params }).then((r) => setRows(r.data));
  };

  useEffect(load, [denom, substrate]);

  const canCreate = ["admin","sustainability","production"].includes(user?.role);

  return (
    <div data-testid="batches-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-emerald-800">Batch Carbon Passports</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Batches</h1>
          <p className="text-sm text-slate-600 mt-2">Each batch carries a verified PCF passport — its sustainability identity card.</p>
        </div>
        {canCreate && (
          <button
            data-testid="new-batch-btn"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900"
          >
            <Plus className="w-4 h-4" /> New Batch
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="border border-slate-200 p-4 bg-white flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500" />
        <select
          data-testid="filter-denomination"
          value={denom}
          onChange={(e) => setDenom(e.target.value)}
          className="px-3 py-2 border border-slate-300 text-sm bg-white"
        >
          <option value="">All denominations</option>
          {[20,50,100,200,500,1000].map((d) => <option key={d} value={d}>₱{d}</option>)}
        </select>
        <select
          data-testid="filter-substrate"
          value={substrate}
          onChange={(e) => setSubstrate(e.target.value)}
          className="px-3 py-2 border border-slate-300 text-sm bg-white"
        >
          <option value="">All substrates</option>
          <option value="polymer">Polymer</option>
          <option value="paper">Paper</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{rows.length} batches</span>
      </div>

      {/* Create form inline */}
      {open && (
        <div data-testid="new-batch-panel" className="border border-emerald-200 bg-emerald-50/40 p-6">
          <div className="overline text-emerald-800 mb-3">Create batch</div>
          <NewBatchForm onCreated={load} onClose={() => setOpen(false)} />
        </div>
      )}

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table data-testid="batches-table" className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline text-slate-500">Batch</th>
              <th className="px-4 py-3 overline text-slate-500">Denom</th>
              <th className="px-4 py-3 overline text-slate-500">Substrate</th>
              <th className="px-4 py-3 overline text-slate-500 text-right">PCF (tCO₂e)</th>
              <th className="px-4 py-3 overline text-slate-500 text-right">kg/note</th>
              <th className="px-4 py-3 overline text-slate-500 text-right">Spoilage</th>
              <th className="px-4 py-3 overline text-slate-500">Hotspot</th>
              <th className="px-4 py-3 overline text-slate-500 text-right">Conf.</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b, i) => (
              <tr key={b.id} data-testid={`batch-row-${b.id}`} className={`${i % 2 ? "bg-slate-50/50" : ""} border-b border-slate-100 hover:bg-emerald-50/40`}>
                <td className="px-4 py-3 font-mono font-semibold text-slate-900">{b.batch_code}</td>
                <td className="px-4 py-3">₱{b.denomination}</td>
                <td className="px-4 py-3">
                  <Pill color={b.substrate === "polymer" ? "emerald" : "amber"}>{b.substrate}</Pill>
                </td>
                <td className="px-4 py-3 text-right font-mono">{b.total_pcf_tco2e?.toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{b.pcf_per_note_kgco2e?.toFixed(5)}</td>
                <td className="px-4 py-3 text-right font-mono">{b.spoilage_rate?.toFixed(2)}%</td>
                <td className="px-4 py-3 text-xs">{b.main_hotspot}</td>
                <td className="px-4 py-3 text-right font-mono">{b.confidence_score}</td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/batches/${b.id}`}
                    data-testid={`open-batch-${b.id}`}
                    className="inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-950 text-xs uppercase tracking-[0.18em]"
                  >
                    Passport <ArrowRight className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-500">No batches found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
